/**
 * @fileoverview Reusable confirmation popover for destructive or high-risk actions.
 * @module shared/components/ui/ConfirmPopover
 */

import { cloneElement, useState, type ReactElement } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'

type TriggerElement = ReactElement<{ disabled?: boolean }>

/**
 * Props for {@link ConfirmPopover}.
 */
export interface ConfirmPopoverProps {
  /** Element used to open the confirmation popover. */
  trigger: TriggerElement
  /** Title displayed inside the confirmation UI. */
  title: string
  /** Optional supporting copy providing additional context. */
  description?: string
  /** Label for the primary confirmation button. */
  confirmLabel: string
  /** Label for the cancellation button. Defaults to `Cancel`. */
  cancelLabel?: string
  /** Invoked when the user confirms the action. */
  onConfirm: () => Promise<void> | void
  /** Invoked when the user cancels the action. */
  onCancel?: () => void
  /** Disable the trigger entirely. */
  disabled?: boolean
  /** Render the confirm button as destructive when true. Defaults to true. */
  destructive?: boolean
}

/**
 * Confirmation popover composed from Radix Popover primitives.
 *
 * @component
 * @example
 * ```tsx
 * <ConfirmPopover
 *   trigger={<Button variant="destructive">Remove</Button>}
 *   title="Remove product group?"
 *   description="All associated SKUs will become ungrouped."
 *   confirmLabel="Remove"
 *   onConfirm={() => void handleRemove()}
 * />
 * ```
 */
export function ConfirmPopover({
  trigger,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  disabled = false,
  destructive = true,
}: ConfirmPopoverProps): ReactElement {
  const [open, setOpen] = useState<boolean>(false)

  const handleOpenChange = (nextOpen: boolean): void => {
    if (disabled) return
    setOpen(nextOpen)
  }

  const handleCancel = (): void => {
    setOpen(false)
    onCancel?.()
  }

  const handleConfirm = async (): Promise<void> => {
    try {
      await onConfirm()
    } finally {
      setOpen(false)
    }
  }

  const triggerElement = cloneElement(trigger, {
    disabled: trigger.props.disabled ?? disabled,
  })

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
      <PopoverContent align="end" className="w-[18rem] space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={handleCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            size="sm"
            onClick={() => {
              void handleConfirm()
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default ConfirmPopover
